import { useEffect, useRef } from "react";
import * as THREE from "three";
import styled, { keyframes } from "styled-components";
import corner from "../../assets/corner.png";
import { openInNewTab } from "../../utils/openInNewTab";
import { useTranslation } from "../../lang/TranslationContext";

export const StarsAnimation = () => {
  const mountRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const { translations, language } = useTranslation();

  useEffect(() => {
    let mouse = {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    };

    let scene: THREE.Scene | undefined;
    let camera: THREE.PerspectiveCamera | undefined;
    let renderer: THREE.WebGLRenderer | null | undefined;
    let animationId: number | undefined;

    function getMousePos(event: MouseEvent) {
      return {
        x: event.clientX,
        y: event.clientY,
      };
    }

    const mouseMoveHandler = (event: MouseEvent) => {
      if (window.innerWidth > 700) {
        mouse = getMousePos(event);
      }
    };

    window.addEventListener("mousemove", mouseMoveHandler);

    scene = new THREE.Scene();
    scene.fog = new THREE.Fog("#000000", 750, 1000);

    camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );

    renderer = rendererRef.current;
    if (!renderer) {
      renderer = new THREE.WebGLRenderer();
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(window.devicePixelRatio);
      rendererRef.current = renderer;
    }

    if (mountRef.current && !mountRef.current.contains(renderer.domElement)) {
      mountRef.current.innerHTML = "";
      mountRef.current.appendChild(renderer.domElement);
    }

    const stars: any[] = [];
    const starCount = 5000;
    const asteroids: any[] = [];
    let cameraZ = 0;
    let lightSpeed = 1;
    let speedStop = false;

    function randint(min: number, max: number) {
      return Math.floor(Math.random() * (max - min)) + min;
    }

    function newStar() {
      const gr: any = {};
      gr.geometry = new THREE.SphereGeometry(1, 8, 8);
      gr.material = new THREE.MeshBasicMaterial({ color: "white" });
      gr.sphere = new THREE.Mesh(gr.geometry, gr.material);
      scene!.add(gr.sphere);

      const w = window.innerWidth;
      const h = window.innerHeight;
      const w2 = w / 2;
      const h2 = h / 2;

      gr.sphere.position.x = randint(-w2, w);
      gr.sphere.position.y = randint(-h2, h);
      gr.sphere.position.z = cameraZ - 1000;

      return gr;
    }

    function mainloop() {
      const isMobile = window.innerWidth <= 768;
      const starsToGenerate = isMobile ? 2 : 10;

      if (stars.length < starCount) {
        for (let i = 0; i < starsToGenerate; i++) {
          stars.push(newStar());
        }
      }

      const visibleStars: any[] = [];

      for (let i = 0; i < stars.length; i++) {
        const star = stars[i];
        star.sphere.scale.z = lightSpeed;

        if (star.sphere.position.z > cameraZ) {
          scene!.remove(star.sphere);
          star.geometry.dispose();
          star.material.dispose();
        } else {
          visibleStars.push(star);
        }
      }

      // Replace with filtered array (cheap operation)
      stars.length = 0;
      stars.push(...visibleStars);

      // Same cleanup approach for asteroids
      const visibleAsteroids: any[] = [];
      for (let i = 0; i < asteroids.length; i++) {
        const asteroid = asteroids[i];
        asteroid.shape.rotation.x += 0.01;
        asteroid.shape.rotation.y += 0.01;

        if (asteroid.shape.position.z > cameraZ) {
          scene!.remove(asteroid.shape);
          asteroid.geometry.dispose();
          asteroid.material.dispose();
        } else {
          visibleAsteroids.push(asteroid);
        }
      }

      asteroids.length = 0;
      asteroids.push(...visibleAsteroids);

      // Speed / camera logic unchanged
      if (!speedStop) {
        if (lightSpeed > 1) lightSpeed += 2;
        if (lightSpeed > 300) speedStop = true;
      } else {
        lightSpeed -= 5;
        if (lightSpeed < 2) {
          speedStop = false;
          lightSpeed = 1;
        }
      }

      if (lightSpeed > 150) {
        cameraZ -= lightSpeed / 1.5;
      } else {
        cameraZ -= 1.5;
      }

      const lookX = -(mouse.x - window.innerWidth / 2) / 250;
      const lookY = (mouse.y - window.innerHeight / 2) / 250;
      const lookAt = new THREE.Vector3(lookX, lookY, cameraZ - 10);
      camera!.position.z = cameraZ;
      camera!.lookAt(lookAt);
    }

    function render() {
      animationId = requestAnimationFrame(render);
      mainloop();
      renderer!.render(scene!, camera!);
    }

    render();

    function onWindowResize() {
      if (!camera || !renderer) return;
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    }

    window.addEventListener("resize", onWindowResize);

    return () => {
      window.removeEventListener("mousemove", mouseMoveHandler);
      window.removeEventListener("resize", onWindowResize);

      if (animationId) {
        cancelAnimationFrame(animationId);
      }

      if (rendererRef.current) {
        rendererRef.current.dispose();
        rendererRef.current = null;
      }

      // Remove all children from the scene and dispose their geometries/materials
      if (scene) {
        while (scene.children.length > 0) {
          const object = scene.children[0];
          if (object instanceof THREE.Mesh) {
            object.geometry.dispose();
            if (Array.isArray(object.material)) {
              object.material.forEach((m) => m.dispose());
            } else {
              object.material.dispose();
            }
          }
          console.log(1);

          scene.remove(object);
        }
      }

      if (mountRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        mountRef.current.innerHTML = "";
      }
    };
  }, []);

  return (
    <MainWrap>
      <LogoWrap>
        <HeroHeading higherCorner={language === "ru"}>
          <h1>{translations.landing.hero.heading1}</h1>
          <h1>
            <span>
              {translations.landing.hero.heading2}
              <div>
                <img src={corner} alt="corner" />
              </div>
            </span>
          </h1>
        </HeroHeading>
        <SubHeading>{translations.landing.hero.subheading}</SubHeading>
        <FreeText>
          {translations.landing.hero.freeStart} •{" "}
          {translations.landing.hero.noCard}
        </FreeText>
        <ButtonGroup>
          <PrimaryButton
            onClick={() => openInNewTab("https://marketecho.app/signup")}
          >
            {translations.landing.hero.cta}
          </PrimaryButton>
        </ButtonGroup>
        <SocialProof>{translations.landing.hero.socialProof}</SocialProof>
      </LogoWrap>
      <Stars ref={mountRef} />
    </MainWrap>
  );
};

const MainWrap = styled.div`
  background: #000;
`;

const simpleFadeInAnimation = keyframes`
0% {
            opacity: 0;
}
100% {
            opacity: 1;
  }
`;
const fadeInAnimation = keyframes`
0% {
    filter: blur(3px);
    -webkit-transform: scale(0.7);
            transform: scale(0.7);
            opacity: 0;
}
30% {
    filter: blur(0px);
    -webkit-transform: scale(0.75);
            transform: scale(0.75);
            opacity: 0.7;
  }
  100% {
    -webkit-transform: scale(1);
            transform: scale(1);
            opacity: 1  ;
  }
`;

const Stars = styled.div`
  margin: 0;
  width: 100%;
  height: 100vh;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

const HeroHeading = styled.div<{ higherCorner: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 20px;

  h1 {
    display: flex;
    font-weight: 400;
    font-size: 60px;
    color: #fff;
    line-height: 1.2;
    margin: 0;

    span {
      font-weight: 800;
      display: flex;
      position: relative;

      > div {
        width: 40px;
        transform: ${({ higherCorner }) =>
          `translate(-20px, -${higherCorner ? "23px" : "15px"})`};

        @media screen and (max-width: 700px) {
          transform: scale(0.8)
            ${({ higherCorner }) =>
              `translate(-20px, -${higherCorner ? "23px" : "15px"})`};
        }

        img {
          width: 100%;

          @media screen and (max-width: 1100px) {
            transform: translateY(5px) scale(0.8);
          }

          @media screen and (max-width: 870px) {
            display: none;
          }
        }
      }
    }

    @media screen and (max-width: 1100px) {
      font-size: 48px;
    }

    @media screen and (max-width: 700px) {
      font-size: 36px;
    }

    @media screen and (max-width: 500px) {
      font-size: 28px;
    }
  }

  @media screen and (max-width: 700px) {
    align-items: flex-start;
    text-align: left;
  }
`;

const SubHeading = styled.p`
  color: #d4d4d4;
  font-size: 20px;
  line-height: 1.6;
  max-width: 800px;
  text-align: center;
  margin: 0 0 8px 0;
  animation: ${simpleFadeInAnimation} 2.5s cubic-bezier(0.39, 0.575, 0.565, 1)
    both;
  animation-delay: 2.5s;

  @media screen and (max-width: 700px) {
    font-size: 16px;
    text-align: left;
    max-width: 350px;
  }
`;

const FreeText = styled.p`
  color: #868686 !important;
  font-size: 15px !important;
  font-weight: 300 !important;
  margin: 8px 0 !important;
  animation: ${simpleFadeInAnimation} 2.5s cubic-bezier(0.39, 0.575, 0.565, 1)
    both;
  animation-delay: 2.7s;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  animation: ${simpleFadeInAnimation} 2s cubic-bezier(0.39, 0.575, 0.565, 1)
    both;
  animation-delay: 3s;

  @media screen and (max-width: 500px) {
    flex-direction: column;
    width: 100%;
    max-width: 300px;
  }
`;

const PrimaryButton = styled.button`
  cursor: pointer;
  padding: 14px 28px;
  border: none;
  background: #047857;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  transition: 0.2s;
  box-shadow: 0 4px 12px rgba(4, 120, 87, 0.3);

  &:hover {
    background: #036146;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(4, 120, 87, 0.4);
  }

  @media screen and (max-width: 500px) {
    width: 100%;
  }
`;

const SocialProof = styled.p`
  color: #a8a8a8;
  font-size: 14px;
  font-weight: 300;
  margin-top: 16px;
  animation: ${simpleFadeInAnimation} 2.5s cubic-bezier(0.39, 0.575, 0.565, 1)
    both;
  animation-delay: 3.2s;

  @media screen and (max-width: 700px) {
    text-align: left;
  }
`;

const LogoWrap = styled.div`
  width: 100%;
  height: 100vh;
  z-index: 2;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  -webkit-animation: ${fadeInAnimation} 5s cubic-bezier(0.39, 0.575, 0.565, 1)
    both;
  animation: ${fadeInAnimation} 5s cubic-bezier(0.39, 0.575, 0.565, 1) both;

  @media screen and (max-width: 700px) {
    align-items: flex-start;
    padding: 0 30px;
  }
`;
